import { useMemo } from 'react';
import { Position } from '../../../../hooks/usePositions';
import { PricesByProductId } from '../../../../hooks/useProductPricesSub';
import { Gwei } from '../../../../lib/gwei';
import {
  calcLiquidationPrice,
  CalcLiquidationPriceParams,
  calcProjectedMaintenanceMargin,
  calcProjectedTotalBalance,
} from '../../../../lib/marginHelpers';
import { add, multiply, safeDivide } from '../../../../lib/math';
import { calcPriceImpact } from '../../../../lib/orderbookHelpers';
import { isOppositeSide, newPositionSize } from '../../../../lib/positionHelpers';

export function useCurrentPosition(positions: Position[] | undefined, productId: string) {
  return useMemo(() => {
    const position = positions?.find((pos) => pos.productId === productId);
    return {
      position,
      size: position?.size || 0n,
      avgEntryPrice: position?.avgPrice || 0n,
    };
  }, [positions, productId]);
}

export function useNewPositionSize(
  currentSize: bigint | undefined,
  amount: bigint | undefined,
  side: 'long' | 'short'
) {
  return useMemo(() => {
    if (currentSize === undefined || amount === undefined) return undefined;
    return newPositionSize(currentSize, amount, side);
  }, [currentSize, amount, side]);
}

export function useProjectedSubaccountBalance({
  subaccountTotalBalance,
  currentPositionSize,
  newPositionSize,
  avgEntryPrice,
  executionPrice,
  feeRate,
}: {
  subaccountTotalBalance: bigint | undefined;
  currentPositionSize: bigint | undefined;
  newPositionSize: bigint | undefined;
  avgEntryPrice: bigint | undefined;
  executionPrice: bigint | undefined;
  feeRate: bigint | undefined;
}) {
  return useMemo(() => {
    if (
      subaccountTotalBalance === undefined ||
      currentPositionSize === undefined ||
      newPositionSize === undefined ||
      avgEntryPrice === undefined ||
      executionPrice === undefined ||
      feeRate === undefined
    ) {
      return undefined;
    }

    return calcProjectedTotalBalance({
      subaccountTotalBalance,
      currentPositionSize,
      newPositionSize,
      avgEntryPrice,
      executionPrice,
      feeRate,
    });
  }, [
    subaccountTotalBalance,
    currentPositionSize,
    newPositionSize,
    avgEntryPrice,
    executionPrice,
    feeRate,
  ]);
}

export function useTotalPositionValue(
  positions: Position[] | undefined,
  pricesByProductId: PricesByProductId
) {
  return useMemo(() => {
    if (!positions) return undefined;

    return positions.reduce((total, position) => {
      const price = pricesByProductId[position.productId]?.oraclePrice;
      if (!price) return total;
      return add(total, Gwei.abs(multiply(position.size, price)));
    }, 0n);
  }, [positions, pricesByProductId]);
}

export function useProjectedTotalPositionValue(
  totalPositionValue: bigint | undefined,
  orderValue: bigint | undefined,
  side: 'long' | 'short'
) {
  return useMemo(() => {
    if (totalPositionValue === undefined || orderValue === undefined) return undefined;
    return newPositionSize(totalPositionValue, orderValue, side);
  }, [totalPositionValue, orderValue, side]);
}

export const useEffectiveLeverage = ({
  totalPositionValue,
  balance,
}: {
  totalPositionValue: bigint | undefined;
  balance: bigint | undefined;
}) => {
  return useMemo(() => {
    if (totalPositionValue === undefined || balance === undefined) return undefined;
    return safeDivide(totalPositionValue, balance);
  }, [totalPositionValue, balance]);
};

export const useProjectedEffectiveLeverage = ({
  totalPositionValue,
  subaccountTotalBalance,
  currentPositionSize,
  newPosSize,
  currentPosAvgEntryPrice,
  executionPrice,
  feeRate,
  orderValue,
  side,
}: {
  totalPositionValue: bigint | undefined;
  subaccountTotalBalance: bigint | undefined;
  currentPositionSize: bigint;
  newPosSize: bigint | undefined;
  currentPosAvgEntryPrice: bigint;
  executionPrice: bigint | undefined;
  feeRate: bigint;
  orderValue: bigint | undefined;
  side: 'long' | 'short';
}) => {
  const projectedSubaccountBalance = useProjectedSubaccountBalance({
    subaccountTotalBalance,
    currentPositionSize: currentPositionSize,
    newPositionSize: newPosSize,
    avgEntryPrice: currentPosAvgEntryPrice,
    executionPrice,
    feeRate,
  });
  const projectedTotalPositionValue = useProjectedTotalPositionValue(
    totalPositionValue,
    orderValue,
    side
  );
  return useEffectiveLeverage({
    totalPositionValue: projectedTotalPositionValue,
    balance: projectedSubaccountBalance,
  });
};

export function useProjectedMaintenanceMargin({
  maintenanceMargin,
  currentPositionSize,
  newPositionSize,
  oraclePrice,
  maintenanceMarginRatio,
}: {
  maintenanceMargin: bigint | undefined;
  currentPositionSize: bigint | undefined;
  newPositionSize: bigint | undefined;
  oraclePrice: bigint | undefined;
  maintenanceMarginRatio: number | undefined;
}) {
  return useMemo(() => {
    if (
      maintenanceMargin === undefined ||
      currentPositionSize === undefined ||
      newPositionSize === undefined ||
      oraclePrice === undefined ||
      maintenanceMarginRatio === undefined
    ) {
      return undefined;
    }

    return calcProjectedMaintenanceMargin({
      maintenanceMargin,
      currentPositionSize,
      newPositionSize,
      oraclePrice,
      maintenanceMarginRatio,
    });
  }, [
    maintenanceMargin,
    currentPositionSize,
    newPositionSize,
    oraclePrice,
    maintenanceMarginRatio,
  ]);
}

export const useMarginUsage = ({
  maintenanceMargin,
  subaccountTotalBalance,
  accountUPnl,
}: {
  maintenanceMargin: bigint | undefined;
  subaccountTotalBalance: bigint | undefined;
  accountUPnl: bigint | undefined;
}) => {
  return useMemo(() => {
    if (
      maintenanceMargin === undefined ||
      subaccountTotalBalance === undefined ||
      accountUPnl === undefined
    ) {
      return undefined;
    }
    return safeDivide(maintenanceMargin, add(subaccountTotalBalance, accountUPnl));
  }, [maintenanceMargin, subaccountTotalBalance, accountUPnl]);
};

export const useProjectedMarginUsage = ({
  maintenanceMargin,
  subaccountTotalBalance,
  currentPositionSize,
  newPositionSize,
  oraclePrice,
  maintenanceMarginRatio,
  avgEntryPrice,
  executionPrice,
  feeRate,
}: {
  maintenanceMargin: bigint | undefined;
  subaccountTotalBalance: bigint | undefined;
  currentPositionSize: bigint;
  newPositionSize: bigint | undefined;
  oraclePrice: bigint | undefined;
  maintenanceMarginRatio: number;
  avgEntryPrice: bigint;
  executionPrice: bigint | undefined;
  feeRate: bigint;
}) => {
  const projectedMaintenanceMargin = useProjectedMaintenanceMargin({
    maintenanceMargin,
    currentPositionSize,
    newPositionSize,
    oraclePrice,
    maintenanceMarginRatio,
  });
  const projectedSubaccountBalance = useProjectedSubaccountBalance({
    subaccountTotalBalance,
    currentPositionSize,
    newPositionSize,
    avgEntryPrice,
    executionPrice,
    feeRate,
  });
  return useMarginUsage({
    maintenanceMargin: projectedMaintenanceMargin,
    subaccountTotalBalance: projectedSubaccountBalance,
    // The pnl is reflected in the projected values
    accountUPnl: 0n,
  });
};

export function useLiquidationPrice({
  oraclePrice,
  accountEquity,
  positionSize,
  maintenanceMarginRatio,
}: Partial<CalcLiquidationPriceParams>) {
  return useMemo(() => {
    if (!positionSize || !accountEquity || !oraclePrice || !maintenanceMarginRatio) {
      return undefined;
    }

    return calcLiquidationPrice({
      oraclePrice,
      accountEquity,
      positionSize,
      maintenanceMarginRatio,
    });
  }, [positionSize, accountEquity, oraclePrice, maintenanceMarginRatio]);
}

export function usePriceImpact(
  limitPrice: bigint | undefined,
  midPrice: bigint | undefined,
  side: 'long' | 'short'
) {
  return useMemo(() => {
    if (limitPrice === undefined || midPrice === undefined) return undefined;
    const impact = calcPriceImpact(limitPrice, midPrice, side === 'long' ? 'bid' : 'ask');
    return impact ?? undefined;
  }, [limitPrice, midPrice, side]);
}

export const useOrderFee = ({
  executionPrice,
  orderSize,
  feeRate,
}: {
  executionPrice: bigint | undefined;
  orderSize: bigint | undefined;
  feeRate: bigint | undefined;
}) =>
  useMemo(() => {
    if (executionPrice === undefined || orderSize === undefined || feeRate === undefined) {
      return undefined;
    }
    return multiply(multiply(orderSize, executionPrice), feeRate);
  }, [executionPrice, orderSize, feeRate]);

export function calcImmediateUnrealizedPnl({
  executionPrice,
  oraclePrice,
  currentPositionSize,
  newPositionSize,
}: {
  executionPrice: bigint;
  oraclePrice: bigint;
  currentPositionSize: bigint;
  newPositionSize: bigint;
}) {
  // Calculate equity impact from execution vs oracle price differences

  const isReducing = Gwei.abs(newPositionSize) < Gwei.abs(currentPositionSize);
  const isFlipping = isOppositeSide(currentPositionSize, newPositionSize);

  if (isReducing) {
    const reductionSize = Gwei.abs(currentPositionSize) - Gwei.abs(newPositionSize);
    const isLong = currentPositionSize > 0n;
    const priceDiff = isLong ? executionPrice - oraclePrice : oraclePrice - executionPrice;
    return multiply(priceDiff, reductionSize);
  }
  if (isFlipping) {
    // Close existing + open new
    const closingSize = Gwei.abs(currentPositionSize);
    const openingSize = Gwei.abs(newPositionSize);
    const wasLong = currentPositionSize > 0n;
    const isLong = newPositionSize > 0n;

    const closingPriceDiff = wasLong ? executionPrice - oraclePrice : oraclePrice - executionPrice;
    const closingImpact = multiply(closingPriceDiff, closingSize);

    const openingPriceDiff = isLong ? oraclePrice - executionPrice : executionPrice - oraclePrice;
    const openingImpact = multiply(openingPriceDiff, openingSize);

    return closingImpact + openingImpact;
  }
  // Increasing position
  const newOrderSize = Gwei.abs(newPositionSize) - Gwei.abs(currentPositionSize);
  const isLong = newPositionSize > 0n;
  const priceDiff = isLong ? oraclePrice - executionPrice : executionPrice - oraclePrice;

  return multiply(priceDiff, newOrderSize);
}

/**
 * Calculates liquidation price after order execution, accounting for:
 * - Order fees
 * - PnL impact from execution price vs oracle price differences
 */
export const useProjectedLiquidationPrice = ({
  orderSize,
  accountEquity,
  newPositionSize,
  executionPrice,
  oraclePrice,
  feeRate,
  maintenanceMarginRatio,
  currentPositionSize,
}: {
  accountEquity: bigint | undefined;
  orderSize: bigint | undefined;
  newPositionSize: bigint | undefined;
  executionPrice: bigint | undefined;
  oraclePrice: bigint | undefined;
  feeRate: bigint;
  maintenanceMarginRatio: number;
  currentPositionSize: bigint | undefined;
}) => {
  const orderFee = useOrderFee({
    executionPrice,
    orderSize,
    feeRate,
  });

  const immediateUnrealizedPnl = useMemo(() => {
    if (
      executionPrice === undefined ||
      oraclePrice === undefined ||
      currentPositionSize === undefined ||
      newPositionSize === undefined
    ) {
      return undefined;
    }

    return calcImmediateUnrealizedPnl({
      executionPrice,
      oraclePrice,
      currentPositionSize,
      newPositionSize,
    });
  }, [executionPrice, oraclePrice, currentPositionSize, newPositionSize]);

  const projectedAccountEquity = useMemo(() => {
    if (
      accountEquity === undefined ||
      orderFee === undefined ||
      immediateUnrealizedPnl === undefined
    ) {
      return undefined;
    }

    return accountEquity - orderFee + immediateUnrealizedPnl;
  }, [accountEquity, orderFee, immediateUnrealizedPnl]);

  return useLiquidationPrice({
    accountEquity: projectedAccountEquity,
    positionSize: newPositionSize,
    maintenanceMarginRatio,
    oraclePrice,
  });
};
